<template>
  <div class="w-full max-w-full p-2 sm:p-4 lg:p-6">
    <!-- Page Title -->
    <div class="font-semibold text-lg sm:text-xl lg:text-2xl mb-3 sm:mb-4 lg:mb-6 text-gray-800">
      <div v-if="props.callFrom === 'all'">Division student list</div>
      <div v-else-if="props.callFrom === 'ParentDB'">Your Children's List</div>
      <div v-else>School student list</div>
    </div>

    <!-- Responsive DataTable Container -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <DataTable
        :value="customers1"
        :paginator="true"
        :rows="10"
        dataKey="id"
        :rowHover="true"
        v-model:filters="filters1"
        @filter="onTableFilter"
        filterDisplay="menu"
        :loading="loading1"
        :filters="filters1"
        :globalFilterFields="[
          'fname',
          'mname',
          'lname',
          'gender',
          'grade_level',
          'phone_number',
          'address1',
          'address2',
          'brgy',
        ]"
        showGridlines
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        :rowsPerPageOptions="[5, 10, 25, 100, 1000, 10000]"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} students"
        class="responsive-datatable"
      >
        <template #header>
          <!-- Mobile Header (Stacked Layout) -->
          <div class="block lg:hidden p-3 space-y-3 bg-gray-50 border-b">
            <!-- Search -->
            <div class="w-full">
              <IconField>
                <InputIcon>
                  <i class="pi pi-search text-sm" />
                </InputIcon>
                <InputText
                  v-model="filters1['global'].value"
                  placeholder="Search students..."
                  class="w-full text-sm"
                />
              </IconField>
            </div>

            <!-- Action Button -->
            <div class="w-full">
              <Button
                v-if="addStudents || authStore.roles.includes('addStudents')"
                label="Add Student"
                severity="info"
                raised
                @click="router.push({ path: '/student/add', query: { isOverAll: true, schoolXid: props.schoolIdx } })"
                class="w-full text-sm"
                icon="pi pi-plus"
                size="small"
              />
            </div>
          </div>

          <!-- Desktop Header (Original Layout) -->
          <div class="hidden lg:flex lg:justify-between lg:items-center p-4 bg-gray-50 border-b">
            <div class="flex-1">
              <IconField>
                <InputIcon>
                  <i class="pi pi-search" />
                </InputIcon>
                <InputText
                  v-model="filters1['global'].value"
                  placeholder="Keyword Search"
                  class="w-80 xl:w-96"
                />
              </IconField>
            </div>

            <div class="ml-4">
              <Button
                v-if="addStudents || authStore.roles.includes('addStudents')"
                label="Add Student"
                severity="info"
                raised
                @click="router.push({ path: '/student/add', query: { isOverAll: true, schoolXid: props.schoolIdx } })"
                class="whitespace-nowrap"
              />
            </div>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-6 text-gray-500">
            <i class="pi pi-users text-3xl mb-3 block text-gray-400"></i>
            <p class="text-base font-medium">No students found</p>
            <p class="text-sm text-gray-400">Try adjusting your search criteria</p>
          </div>
        </template>

        <template #loading>
          <div class="text-center py-6">
            <i class="pi pi-spin pi-spinner text-2xl text-blue-500 mb-2"></i>
            <p class="text-gray-600">Loading student data. Please wait.</p>
          </div>
        </template>

        <!-- Name Column - Always visible -->
        <Column
          field="name"
          filterField="first_name"
          filter-field="last_name"
          header="Name"
          style="min-width: 180px"
          frozen
          class="name-column"
        >
          <template #body="{ data }">
            <div class="text-xs sm:text-sm lg:text-base font-medium text-gray-900 leading-tight">
              <div class="truncate max-w-[120px] sm:max-w-[160px] lg:max-w-none" :title="data.mname ? `${data.lname}, ${data.fname} ${data.mname}` : `${data.lname}, ${data.fname}`">
                {{ data.mname ? `${data.lname}, ${data.fname} ${data.mname}` : `${data.lname}, ${data.fname}` }}
              </div>
              <!-- Mobile-only additional info -->
              <div class="block lg:hidden text-xs text-gray-500 mt-1">
                <div>{{ data.gender }} • {{ data.date_of_birth }}</div>
                <div class="truncate">{{ data.address1 }}</div>
              </div>

            <div class="block mt-4 lg:hidden">
              <div class="flex flex-col gap-1">
                <Button
                  v-if="authStore.roles.includes('viewStudents')"
                  type="button"
                  icon="pi pi-eye"
                  label="View"
                  @click="router.push(`/student/profile/${data.pid}`)"
                  severity="primary"
                  size="small"
                  class="w-full text-xs py-1"
                />
                <Button
                  v-if="authStore.roles.includes('viewAttendance')"
                  type="button"
                  icon="pi pi-calendar"
                  label="Attendance"
                  @click="router.push('/student/studentcalendar')"
                  severity="secondary"
                  size="small"
                  class="w-full text-xs py-1"
                />
              </div>
            </div>
            </div>
          </template>
        </Column>

        <!-- Gender Column - Hidden on mobile -->
        <Column
          header="Gender"
          filterField="gender"
          style="min-width: 80px"
          class="hidden lg:table-cell"
        >
          <template #body="{ data }">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="data.gender === 'Male' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'">
              {{ data.gender }}
            </span>
          </template>
        </Column>

        <!-- Status Column - Always visible -->
        <Column
          header="Status"
          filterField="status"
          style="min-width: 100px"
          class="hidden md:table-cell"
        >
          <template #body="{ data }">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="getStatusClass(data.status)">
              {{ data.status }}
            </span>
          </template>
        </Column>

        <!-- Date of Birth Column - Hidden on mobile and tablets -->
        <Column
          header="School Name"
          filterField="date_of_birth"
          style="min-width: 120px"
          class="hidden xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600">
              {{ data.school?.name || 'N/A' }}
            </div>
          </template>
        </Column>

        <!-- Address Column - Hidden on mobile and tablets -->
        <Column
          header="Address"
          filterField="address1"
          style="min-width: 160px"
          class="hidden xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600 truncate max-w-[140px]" :title="data.address1">
              {{ data.address1 || 'N/A' }}
            </div>
          </template>
        </Column>

        <!-- Barangay Column - Hidden on mobile and tablets -->
        <Column
          header="Grade Level"
          filterField="barangay"
          style="min-width: 120px"
          class="hidden 2xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600 truncate max-w-[100px]" :title="data.grade_level?.name">
              {{ data.grade_level?.name || 'N/A' }}
            </div>
          </template>
        </Column>

        <!-- Actions Column - Always visible -->
        <Column
          header="Actions"
          bodyClass="text-center"
          style="min-width: 120px"
          class="hidden md:table-cell"
        >
          <template #body="{ data }">
            <!-- Mobile Actions - Stacked -->
            <div class="block lg:hidden">
              <div class="flex flex-col gap-1">
                <Button
                  v-if="authStore.roles.includes('viewStudents')"
                  type="button"
                  icon="pi pi-eye"
                  label="View"
                  @click="router.push(`/student/profile/${data.pid}`)"
                  severity="primary"
                  size="small"
                  class="w-full text-xs py-1"
                />
                <Button
                  v-if="authStore.roles.includes('viewAttendance')"
                  type="button"
                  icon="pi pi-calendar"
                  label="Attendance"
                  @click="router.push('/student/studentcalendar')"
                  severity="secondary"
                  size="small"
                  class="w-full text-xs py-1"
                />
              </div>
            </div>

            <!-- Desktop Actions - Horizontal -->
            <div class="hidden lg:flex justify-center items-center gap-2">
              <Button
                v-if="authStore.roles.includes('viewStudents') || props.callFrom === 'ParentDB'"
                type="button"
                icon="pi pi-eye"
                label="View"
                @click="router.push(`/student/profile/${data.pid}`)"
                severity="primary"
                size="small"
              />
              <Button
                v-if="authStore.roles.includes('viewAttendance')"
                type="button"
                icon="pi pi-calendar"
                label="Attendance"
                @click="router.push('/student/studentcalendar')"
                severity="secondary"
                size="small"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import { reactive, ref, watchEffect, computed, defineProps, onBeforeMount } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/router'

import { usePermissionStore } from '../../store/permissionsStore'
import { useAuthStore } from '../../auth/auth'
import { usedStudentStore } from '@/store/admin/StudentStore'

const studentStore = usedStudentStore()

const props = defineProps({
  callFrom: {
    type: String,
    required: true,
  },
  schoolIdx: {
    type: String,
  },
})

const { students } = storeToRefs(studentStore)


onBeforeMount( async () =>{
  loading1.value = true
// Fetch data based on props.callFrom
if (props.callFrom === 'all') {
 await studentStore.fetchData()
}else if(props.callFrom === 'ParentDB'){
  // studentStore.fetchByParentID(props.schoolIdx)
}
 else {
  await studentStore.fetchDataBySchool(props.schoolIdx)
}
  loading1.value = false

})
const authStore = useAuthStore()
const permissionsStore = usePermissionStore()

const addStudents = computed(() =>
  permissionsStore.checkPermission('addStudents', authStore.userType),
)

// Status styling helper
const getStatusClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'enrolled':
      return 'bg-green-100 text-green-800'
    case 'inactive':
    case 'drop':
    case 'dropped':
      return 'bg-red-100 text-red-800'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800'
    case 'transferred':
      return 'bg-blue-100 text-blue-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// Date formatting helper
const formatDate = (dateString) => {
  if (!dateString) return null
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    })
  } catch (error) {
    return dateString
  }
}

const display = ref(false)
const visibleTop = ref(false)

function open(id) {
  const selected = students.value.find((student) => student.id === id)
  studentStore.addstud(selected)
  if (selected) {
    formselect.name = selected.name
    formselect.age = selected.age
    formselect.birthdate = selected.birthdate
    formselect.gender = selected.gender
    formselect.avegrade = selected.avegrade
    formselect.status = selected.status
    formselect.pass = selected.pass
    formselect.mother = selected.representative?.mother
    formselect.father = selected.representative?.father
    formselect.phonenumber = selected.phonenumber
    formselect.grade = selected.grade
    formselect.section = selected.section
  } else {
    alert('Student not found!')
  }
  display.value = true
}

function close() {
  display.value = false
  visibleTop.value = true
}

const statuses = reactive(['Drop', 'Active'])
const customers1 = ref(null)
const filters1 = ref(null)
const loading1 = ref(false)

const formselect = reactive({
  First: '',
  grade: '',
  section: '',
  gender: '',
  mother: '',
  father: '',
  birthdate: '',
  avegrade: '',
  status: '',
  phonenumber: '',
  age: 0,
  pass: false
})

const displayedStudents = ref([])

function initFilters1() {
  filters1.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    fname: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    lanme: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    mname: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    phone_number: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    grade_level: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    status: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    gender: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    email_address: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address1: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address2: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    barangay: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address_1: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
  }
}

function getSeverity(status) {
  switch (status) {
    case 'Drop':
      return 'danger'
    case 'Active':
      return 'success'
    case 'renewal':
      return null
  }
}

const onTableFilter = (event) => {
  displayedStudents.value = event.filteredValue
}

watchEffect(() => {
  customers1.value = students.value
  if (!filters1.value) {
    initFilters1()
  }
})
</script>

<style scoped>
.form-container {
  min-width: unset; /* Remove fixed min-width for mobile responsiveness */
  margin: 0 auto;
  padding: 1rem; /* Add padding for better spacing on all screens */
  max-width: 1200px; /* Optional: Constrain max width on large screens */
}

/* Custom styles for DataTable responsiveness on mobile */
/* These styles will kick in for smaller screens to make the table columns stack */
@media screen and (max-width: 960px) {
  .p-datatable.p-datatable-responsive-mobile .p-datatable-thead > tr > th,
  .p-datatable.p-datatable-responsive-mobile .p-datatable-tfoot > tr > td {
    display: none !important; /* Hide original table headers/footers */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td {
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between; /* Space out label and value */
    width: 100%;
    padding: 0.5rem 1rem;
    border: 0 none;
    border-top: 1px solid var(--surface-d); /* Separator between "rows" of data */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td:last-child {
    border-bottom: 1px solid var(--surface-d); /* Bottom border for the last cell in a stacked row */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td:first-child {
    background: var(--surface-b); /* Highlight the first item (e.g., Name) */
    font-weight: 600;
  }

  /* Label for each row data - uses the 'data-p-field' attribute from PrimeVue's internal structure */
  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td::before {
    content: attr(data-p-field) ":"; /* Displays the column header as a label */
    font-weight: 600;
    margin-right: 0.5rem;
    display: inline-block;
  }
}

/* General layout adjustments for smaller screens */
@media (max-width: 768px) {
  /* Ensure header elements stack nicely */
  .p-datatable-header .flex {
    flex-direction: column;
    align-items: flex-start; /* Align stacked items to the start */
  }

  /* Make sure individual inputs and buttons within header take full width on mobile */
  .p-datatable-header .p-inputtext,
  .p-datatable-header .p-multiselect, /* (if MultiSelect was here) */
  .p-datatable-header .p-button {
    width: 100%;
  }

  /* Adjust gaps for stacked elements */
  .p-datatable-header .flex-column > .flex-column {
    gap: 0.75rem; /* Smaller gap for stacked inner groups */
  }
}
</style>
